import {Action, Module, Mutation, VuexModule} from 'vuex-module-decorators';
import {Vue} from 'vue-property-decorator';
import {ILocation, ILocationWithDistance, SessionStore} from '../../store';
import {IBaseResponse, LocationService, WebService} from '../../services';
import {cloneDeep} from 'lodash';
import {openURL, Platform} from 'quasar';

export type TLocationId = ILocation['id'];
export type TMaybeLocation = ILocation | null;
type TLocationIdFunction<T> = (arg: TLocationId | null) => T;

type TResponseFavorite = IBaseResponse<{location: ILocation}>;

export interface ILocationStoreState {
	locations: ILocation[];
}

export enum EMapType {
	COMPACT = 'compact',
	FULL_PAGE = 'full_page'
}

const SEARCH_FIELDS = [
	'city',
	'streetName',
	'state',
	'zipCode',
	'name'
];

@Module({name: 'locations', namespaced: true})
class LocationModule extends VuexModule implements ILocationStoreState {
	locations: ILocation[] = [];

	get location(): TLocationIdFunction<TMaybeLocation> {
		return (id) => {
			if(typeof id === 'string') {
				return this.locations.find((loc) => loc.id === id) ?? null;
			}
			return null;
		};
	}

	get doorDashLocations() {
		return this.locations.filter((location) => location.doorDashURL);
	}

	get favoriteLocations() {
		return this.orderingLocationsSortedByDistance.filter((location) => location.favorite);
	}

	get locationsSortedByDistance(): ILocationWithDistance[] {
		const lastLocation = SessionStore.lastLocation;
		if(lastLocation && lastLocation.coords) {
			return cloneDeep(this.locations).map(location => {
				return {
					...location,
					distance: this.haversineDistance(
						location.latitude,
						location.longitude,
						lastLocation.coords.latitude,
						lastLocation.coords.longitude
					)
				};
			}).sort((a, b) => a.distance - b.distance);
		} else {
			return this.locationsSortedAlphabetically;
		}
	}

	get orderingLocationsSortedByDistance(): ILocationWithDistance[] {
		return this.locationsSortedByDistance.filter((location) => location.orderingAvailable);
	}

	get locationsSortedAlphabetically(): ILocationWithDistance[] {
		return cloneDeep(this.locations).map(location => {
			return {
				...location,
				distance: null
			};
		}).sort((a, b) => {
			return a.name.localeCompare(b.name);
		});
	}

	get distanceToLocation() {
		return async (selectedLocation: ILocation) => {
			const lastLocation = SessionStore.lastLocation;
			if(!lastLocation) {
				await LocationService.getCurrentPosition();
			}
			if(lastLocation && lastLocation.coords) {
				return this.haversineDistance(
					selectedLocation.latitude,
					selectedLocation.longitude,
					lastLocation.coords.latitude,
					lastLocation.coords.longitude);
			}
			return null;
		};
	}

	get haversineDistance() {
		return (lat1: number, long1: number, lat2: number, long2: number) => {
			const earthRadius = 3958.8; // miles

			const diffLat = (lat2 - lat1) * Math.PI / 180;
			const diffLng = (long2 - long1) * Math.PI / 180;

			const arc = Math.cos(
				lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
				Math.sin(diffLng / 2) * Math.sin(diffLng / 2) +
				Math.sin(diffLat / 2) * Math.sin(diffLat / 2);
			const line = 2 * Math.atan2(Math.sqrt(arc), Math.sqrt(1 - arc));

			return earthRadius * line;
		};
	}

	get openLocationAddress() {
		return (location: ILocation) => {
			let url = '';
			if(Platform.is.ios) {
				url = 'http://maps.apple.com/maps?q=' + encodeURIComponent(location.formattedAddress);
			} else if(Platform.is.android) {
				url = 'geo:0,0?q=' + encodeURIComponent(location.formattedAddress);
			} else {
				url = 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(location.formattedAddress);
			}
			console.log(url);
			openURL(url);
		};
	}

	get searchLocations() {
		return (term: string) => {
			return this.locationsSortedByDistance.filter(location => {
				return SEARCH_FIELDS.some(val => {
					const tempTerm = location[val as keyof ILocationWithDistance] as string;
					const regex = new RegExp(`(\\b${term.toLowerCase()})`);
					const regResult = regex.exec(tempTerm.toLowerCase()) || [];
					return regResult.length > 0;
				});
			});	
		};
	}

	get locationAddress1() {
		return (location: ILocationWithDistance) => {
			return (location?.streetNumber ?? '') + ' ' + (location?.streetName ?? '');
		};
	}
	
	get locationAddress2() {
		return (location: ILocationWithDistance) => {
			return (location?.city ?? '') + ', ' + (location?.state ?? '') + ' ' + (location?.zipCode ?? '');
		};
	}

	get distance() {
		return (location: ILocationWithDistance) => {
			const roundedDistance = location.distance ? location.distance.toFixed(1) : null;
			return roundedDistance ? `${roundedDistance} mi` : '';
		};
	}

	@Action
	async favoriteAdd(locationId: TLocationId) {
		const response = await WebService.authenticatedClient.post<TResponseFavorite>(
			`locations/${locationId}/favorite`,
		);
		this.updateLocation(response.data.data.location);
	}

	@Action
	async favoriteDelete(locationId: TLocationId) {
		const response = await WebService.authenticatedClient.post<TResponseFavorite>(
			`locations/${locationId}/favorite/delete`,
		);
		this.updateLocation(response.data.data.location);
	}

	@Action
	async fetchLocations() {
		const response = await WebService.authenticatedClient.get<IBaseResponse<{locations: ILocation[]}>>('locations');
		this.setLocations(response.data.data.locations);
	}

	@Mutation
	public setLocations(locations: ILocation[]) {
		this.locations = locations;
	}

	@Mutation
	public clearLocations() {
		this.locations = [];
	}

	@Mutation
	private updateLocation(location: ILocation) {
		const updateIndex = this.locations.findIndex((loc) => {
			return loc.id === location.id;
		});

		if(updateIndex === -1) {
			this.locations.push(location);
		} else {
			Vue.set(this.locations, updateIndex, location);
		}
	}
}


export {
	LocationModule
};
